/**
 * IndexedDB для кэша фич тактической доски и очереди offline-мутаций.
 * Без внешних зависимостей (паттерн chat-crypto).
 */

import type { TacticalFeature } from "@/services/tactical.service";

const DB_NAME = "geopulseml-tactical-offline";
const DB_VERSION = 1;
const STORE_CACHE = "board_features";
const STORE_QUEUE = "mutation_queue";

export type TacticalOfflineMutation = {
  id: string;
  boardId: number;
  op: "create" | "update" | "delete";
  /** Серверный id или временный (отрицательный) для create. */
  featureId: number;
  payload?: Record<string, unknown>;
  createdAt: string;
};

export type BoardFeaturesCache = {
  boardId: number;
  features: TacticalFeature[];
  savedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "boardId" });
      }
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const store = db.createObjectStore(STORE_QUEUE, { keyPath: "id" });
        store.createIndex("by_board", "boardId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("idb tx error"));
    tx.onabort = () => reject(tx.error ?? new Error("idb tx abort"));
  });
}

export async function putBoardFeaturesCache(
  boardId: number,
  features: TacticalFeature[],
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_CACHE, "readwrite");
  tx.objectStore(STORE_CACHE).put({
    boardId,
    features,
    savedAt: new Date().toISOString(),
  } satisfies BoardFeaturesCache);
  await txDone(tx);
  db.close();
}

export async function getBoardFeaturesCache(
  boardId: number,
): Promise<BoardFeaturesCache | null> {
  const db = await openDb();
  const tx = db.transaction(STORE_CACHE, "readonly");
  const req = tx.objectStore(STORE_CACHE).get(boardId);
  const row = await new Promise<BoardFeaturesCache | undefined>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as BoardFeaturesCache | undefined);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return row ?? null;
}

export async function enqueueMutation(
  mutation: Omit<TacticalOfflineMutation, "id" | "createdAt"> & {
    id?: string;
    createdAt?: string;
  },
): Promise<TacticalOfflineMutation> {
  const row: TacticalOfflineMutation = {
    id: mutation.id ?? crypto.randomUUID(),
    boardId: mutation.boardId,
    op: mutation.op,
    featureId: mutation.featureId,
    payload: mutation.payload,
    createdAt: mutation.createdAt ?? new Date().toISOString(),
  };
  const db = await openDb();
  const tx = db.transaction(STORE_QUEUE, "readwrite");
  tx.objectStore(STORE_QUEUE).put(row);
  await txDone(tx);
  db.close();
  return row;
}

export async function listMutations(boardId: number): Promise<TacticalOfflineMutation[]> {
  const db = await openDb();
  const tx = db.transaction(STORE_QUEUE, "readonly");
  const idx = tx.objectStore(STORE_QUEUE).index("by_board");
  const req = idx.getAll(boardId);
  const rows = await new Promise<TacticalOfflineMutation[]>((resolve, reject) => {
    req.onsuccess = () => resolve((req.result as TacticalOfflineMutation[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function deleteMutation(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE_QUEUE, "readwrite");
  tx.objectStore(STORE_QUEUE).delete(id);
  await txDone(tx);
  db.close();
}

export async function remapQueueFeatureId(
  boardId: number,
  fromId: number,
  toId: number,
): Promise<void> {
  const items = await listMutations(boardId);
  const db = await openDb();
  const tx = db.transaction(STORE_QUEUE, "readwrite");
  const store = tx.objectStore(STORE_QUEUE);
  for (const item of items) {
    if (item.featureId !== fromId) continue;
    store.put({ ...item, featureId: toId });
  }
  await txDone(tx);
  db.close();
}

export function nextTempFeatureId(): number {
  return -Math.abs(Date.now() + Math.floor(Math.random() * 1000));
}
